import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, Trash2 } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useGetAllMessageActivity, useResendMessage } from "../hooks";

interface MessageActivityData {
  id: string;
  type: string;
  channel: string;
  recipient: string;
  audienceType: string;
  status: string;
  sentAt: string;
}

const MessageActivityList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read params from URL
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const currentPage = parseInt(searchParams.get("page") || "1");

  const { data: messageActivityList } = useGetAllMessageActivity(
    currentPage,
    PAGE_SIZE,
    searchQuery
  );

  const resendMessage = useResendMessage()

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination
  React.useEffect(() => {
    const totalItems = messageActivityList?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [messageActivityList, currentPage]);

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    newParams.page = "1";
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  const handleReSend = (activityId: string) => {
    const payload = {
      id:activityId
    };

    resendMessage.mutateAsync(payload);
  };

  const columns: TableColumn<MessageActivityData>[] = [
    {
      key: "type",
      header: "Type",
      sortable: true,
      render: (value: string) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {value}
        </span>
      ),
    },
    {
      key: "channel",
      header: "Channel",
      sortable: true,
      render: (value: string) => <p>{value}</p>,
    },
    {
      key: "recipient",
      header: "Recipient",
      sortable: true,
    },
    {
      key: "audienceType",
      header: "Audience Type",
      sortable: true,
      render: (value: string) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {value || "N/A"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (value: string) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            value === "Sent"
              ? "bg-green-100 text-green-800"
              : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "sendAt",
      header: "Sent At",
      sortable: true,
    },
    {
      key: "sendOn",
      header: "Sent On",
      sortable: true,
    },
  ];

  const actions = [
    {
      label: "View",
      onClick: (row: MessageActivityData) => {
        navigate(`/communication-hub/message-activity/view/${row.id}`);
      },
      icon: <Eye className="h-4 w-4 items-end ml-[140px]" />,
    },
    {
      label: "Re-send",
      onClick: (row: MessageActivityData) => {
       handleReSend(row.id);
      },
      icon: <Eye className="h-4 w-4 items-end ml-[140px]" />,
    },
  ];

  return (
    <div className="space-y-6">
      <GenericTable
        data={messageActivityList?.results || []}
        columns={columns}
        rowKey="id"
        selectable={false}
        actions={actions}
        actionsType="dropdown"
        emptyMessage="No messages found matching your criteria"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by name, email, or phone number..."
      />
    </div>
  );
};

export default MessageActivityList;
